import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { tripPriceDisplay } from '@/utils/tripPriceUtils';
import { ccySym } from '@/utils/currencyUtils';
import { isTripCapacityInsufficient } from '@/utils/missionGuards';
import { targetPrice as computeTargetPrice, hasNoPriceToAccept, isNegotiable, priceLabel } from '@/utils/propositionPricing';
import MatchPropositionForm from '@/pages/matching/MatchPropositionForm';
import '@/styles/matching.css';
import { cityFlag as flag } from '@/utils/cityFlag';
import { isEntreprise } from '@/utils/entreprise';
import { BACKEND_URL } from '@/config';
import VerificationBadges from '@/components/VerificationBadges';
import {
  ArrowLeft, Briefcase, CalendarDays, Inbox, Route, Send, Sparkles, Star, Tag, Trophy, Weight,
} from 'lucide-react';

const API = BACKEND_URL + '/api';
const WINE = '#722f37';
const meSide = 'sender';
axios.defaults.withCredentials = true;

function fmtDate(d) {
  if (!d) return '·';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ScoreBreakdown({ match }) {
  if (!match) return null;
  const items = [
    { key: 'geo',   label: 'Géographie', val: match.geo,   max: 30, tone: 'gold' },
    { key: 'price', label: 'Prix',       val: match.price, max: 25, tone: 'teal' },
    { key: 'time',  label: 'Temps',      val: match.time,  max: 25, tone: 'navy' },
    { key: 'rep',   label: 'Réputation', val: match.rep,   max: 20, tone: 'gold' },
  ];
  return (
    <div className="mt-score-breakdown">
      {items.map(i => (
        <div key={i.key} className="mt-score-cell">
          <div className={`mt-score-val mt-tone-${i.tone}`}>{i.val}</div>
          <div className="mt-score-lbl">{i.label}</div>
        </div>
      ))}
    </div>
  );
}

function ScoreDial({ total }) {
  const pct = Math.min(100, Math.max(0, total));
  const angle = (pct / 100) * 360;
  return (
    <div className="mt-dial" style={{ background: `conic-gradient(var(--gold) ${angle}deg, var(--bdl) 0deg)` }}>
      <div className="mt-dial-inner">
        <div className="mt-dial-val">{pct}</div>
        <div className="mt-dial-lbl">/ 100</div>
      </div>
    </div>
  );
}

export default function ShipmentMatchingPage() {
  const { id: shipmentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [matches, setMatches] = useState([]);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [mode, setMode] = useState('direct'); // 'direct' | 'negotiation'
  const [pendingPairSet, setPendingPairSet] = useState(new Set());

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/shipments/${shipmentId}`),
      axios.get(`${API}/shipments/${shipmentId}/matching?limit=30`),
      axios.get(`${API}/propositions/sent?status=PENDING&limit=50`),
    ])
      .then(([s, m, p]) => {
        setMine(s.data);
        setMatches(m.data?.data || []);
        const ids = (p.data?.data || [])
          .filter(prop => prop.shipment_id === shipmentId)
          .map(prop => prop.trip_id);
        setPendingPairSet(new Set(ids));
      })
      .catch(err => toast.error(err?.response?.data?.detail || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  const expand = async (target, nextMode) => {
    if (expandedId === target.id && mode === nextMode) { setExpandedId(null); return; }
    try {
      const fresh = await axios.get(`${API}/trips/${target.id}`);
      const st = fresh.data?.status;
      if (!['PUBLISHED', 'ACTIVE', 'PENDING_REVIEW'].includes(st)) {
        toast.error('Ce trajet n\'est plus disponible — il a été fermé ou annulé.');
        setMatches(prev => prev.filter(m => m.id !== target.id));
        return;
      }
    } catch { /* réseau KO → le backend gèrera au submit */ }
    setExpandedId(target.id);
    setMode(nextMode);
  };

  if (loading) {
    return (
      <div className="matching-page">
        <div className="mt-loading">Recherche des trajets compatibles…</div>
      </div>
    );
  }

  return (
    <div className="matching-page" data-testid="shipment-matching-page">
      <button className="mt-back" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} aria-hidden="true" /> Retour
      </button>

      <div className="mt-header">
        <div>
          <h1 className="mt-title">
            <Sparkles size={28} aria-hidden="true" /> Matching intelligent
          </h1>
          {mine && (
            <div className="mt-subtitle">
              {flag(mine.departure_city)} {mine.departure_city} → {flag(mine.arrival_city)} {mine.arrival_city} · {mine.weight_kg}kg · {mine.price_offered ? `budget ${mine.price_offered} ${ccySym(mine.primary_currency || 'EUR')}` : 'Budget libre'}
            </div>
          )}
        </div>
        <div className="mt-summary">
          <div className="mt-summary-num">{matches.length}</div>
          <div className="mt-summary-lbl">trajet{matches.length > 1 ? 's' : ''} compatible{matches.length > 1 ? 's' : ''}</div>
        </div>
      </div>

      {matches.length === 0 ? (
        <div className="mt-empty">
          <Inbox size={44} aria-hidden="true" />
          <h3>Aucun trajet compatible pour l'instant</h3>
          <p>Revenez dans quelques jours, ou ajustez la date / le corridor de votre annonce.</p>
        </div>
      ) : (
        <div className="mt-grid">
          {matches.map((target, idx) => {
            const c = target.carrier || {};
            const m = target.match || {};
            const isExpanded = expandedId === target.id;
            const noPrice = hasNoPriceToAccept(meSide, target);
            // Ce que coûterait ce trajet pour mon colis : au kilo × son poids, ou le forfait
            const targetPrice = computeTargetPrice(meSide, target, mine);
            const isBest = idx === 0;
            const negotiable = isNegotiable(meSide, target);
            const isFull = target.available_weight_kg <= 0 || target.status === 'FULL';
            const isInsufficient = isTripCapacityInsufficient(target, mine?.weight_kg);
            const hasPending = pendingPairSet.has(target.id);
            return (
              <div key={target.id} className={`mt-card${isBest ? ' is-best' : ''}`} data-testid={`mt-card-${target.id}`}>
                {isBest && <div className="mt-best-flag"><Trophy size={13} aria-hidden="true" /> Meilleur match</div>}

                <div className="mt-card-head">
                  <ScoreDial total={m.total} />
                  <div className="mt-carrier">
                    <div className="mt-carrier-name">
                      {c.first_name} {c.last_name}
                      <VerificationBadges user={c} compact />
                      {isEntreprise(c) && <span className="mt-badge mt-badge-entreprise"><Briefcase size={11} aria-hidden="true" /> Entreprise</span>}
                    </div>
                    <div className="mt-carrier-meta">
                      {c.avg_rating ? (
                        <span><Star size={12} fill="currentColor" aria-hidden="true" /> {c.avg_rating.toFixed(1)} · {c.rating_count || 0} avis</span>
                      ) : (
                        <span className="mt-muted">Nouveau membre</span>
                      )}
                      {c.completed_missions != null && <span> · {c.completed_missions} missions</span>}
                    </div>
                  </div>
                </div>

                <div className="mt-trip-info">
                  <div className="mt-trip-route">
                    <Route size={15} aria-hidden="true" /> {flag(target.departure_city)} {target.departure_city} → {flag(target.arrival_city)} {target.arrival_city}
                  </div>
                  <div className="mt-trip-meta">
                    <span><CalendarDays size={13} aria-hidden="true" /> {fmtDate(target.departure_date)}</span>
                    <span>
                      <Weight size={13} aria-hidden="true" /> {target.available_weight_kg}kg dispo
                      {isFull && <span style={{ marginLeft: 6, background: '#fef2f2', color: '#b91c1c', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>COMPLET</span>}
                      {isInsufficient && <span style={{ marginLeft: 6, background: '#fffbeb', color: '#d97706', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>⚠ INSUFFISANT</span>}
                    </span>
                    <span><Tag size={13} aria-hidden="true" /> {tripPriceDisplay(target).display}</span>
                  </div>
                </div>

                <ScoreBreakdown match={m} />

                <div className="mt-foot">
                  <div className="mt-price">
                    <span className="mt-price-lbl">Estimation</span>
                    <span className="mt-price-amt">{targetPrice ? `${priceLabel(targetPrice.amount)} ${ccySym(targetPrice.currency)}` : 'Tarif libre'}</span>
                  </div>
                  <div className="mt-actions">
                    <button
                      className="mt-btn mt-btn-ghost"
                      onClick={() => navigate(`/app/trips/${target.id}`)}
                      data-testid={`mt-view-${target.id}`}
                    >
                      Profil
                    </button>
                    {hasPending ? (
                      <div style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', background:'#fffbeb', border:'1px solid #f59e0b', borderRadius:8, fontSize:12 }}>
                        <span>⏳</span>
                        <span style={{ color:'#92400e', fontWeight:600 }}>Proposition en attente</span>
                        <button
                          onClick={() => navigate('/app/propositions')}
                          style={{ background:'none', border:'none', color:'#d97706', cursor:'pointer', textDecoration:'underline', fontSize:12, padding:0 }}
                        >
                          Gérer →
                        </button>
                      </div>
                    ) : isFull ? (
                      <button className="mt-btn mt-btn-ghost" disabled style={{ color:'#9ca3af', cursor:'not-allowed', borderColor:'#e5e7eb' }}>
                        Complet
                      </button>
                    ) : isInsufficient ? (
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
                        <button className="mt-btn mt-btn-ghost" disabled style={{ color:'#d97706', cursor:'not-allowed', borderColor:'#fcd34d', background:'#fffbeb' }} data-testid={`mt-insufficient-${target.id}`}>
                          ⚠ Capacité insuffisante
                        </button>
                        <span style={{ fontSize:11, color:'#92400e' }}>
                          {target.available_weight_kg} kg dispo · colis {mine?.weight_kg} kg
                        </span>
                      </div>
                    ) : (
                      <>
                        {!noPrice && (
                          <button
                            className={`mt-btn ${isExpanded && mode === 'direct' ? 'mt-btn-ghost' : 'mt-btn-primary'}`}
                            onClick={() => expand(target, 'direct')}
                            data-testid={`mt-propose-${target.id}`}
                          >
                            <Send size={14} aria-hidden="true" /> {isExpanded && mode === 'direct' ? 'Annuler' : 'Faire une proposition'}
                          </button>
                        )}
                        {noPrice ? (
                          <button
                            className="mt-btn"
                            style={{ background: isExpanded && mode === 'negotiation' ? '#5a2429' : WINE, color:'#fff', borderColor: WINE }}
                            onClick={() => expand(target, 'negotiation')}
                            data-testid={`mt-negotiate-${target.id}`}
                          >
                            💬 {isExpanded && mode === 'negotiation' ? 'Annuler' : 'Négocier (prix libre)'}
                          </button>
                        ) : negotiable ? (
                          <button
                            className="mt-btn"
                            style={{ background: isExpanded && mode === 'negotiation' ? '#5a2429' : WINE, color:'#fff', borderColor: WINE }}
                            onClick={() => expand(target, 'negotiation')}
                            data-testid={`mt-negotiate-${target.id}`}
                          >
                            💬 {isExpanded && mode === 'negotiation' ? 'Annuler' : 'Négocier'}
                          </button>
                        ) : (
                          <button
                            className="mt-btn mt-btn-ghost"
                            disabled
                            style={{ color:'#9ca3af', cursor:'not-allowed', borderColor:'#e5e7eb' }}
                            data-testid={`mt-non-neg-${target.id}`}
                          >
                            🔒 Non Négociable
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {isExpanded && !hasPending && (
                  <MatchPropositionForm
                    key={`${target.id}-${mode}`}
                    meSide={meSide}
                    target={target}
                    mine={mine}
                    mode={mode}
                    theirFirstName={c.first_name}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
